<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import {
  getActionId,
  getAdditionalProps,
  handleProgressFields,
  handleSuccessfulReg,
  handleUnSuccessfulReg,
} from '~/utilities/form-builder.reg';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import type { IPartialRegStepOneRequest } from '~/interfaces/dto/auth/request';
import type { IRegisterResponse } from '~/interfaces/dto/auth/response';
import { generateTranslationKey } from '~/utilities/data-transformers';
import { getUserDeviceInfo } from '~/utilities/device-info';

const { $t, $trackEvent } = useNuxtApp();
const { $authService } = useServices();
const auth = useAuthStore();
const site = useSiteStore();

const isNamesVerified = ref<boolean>(false);
const form = ref<IFormBuilderReference>();
const loading = ref<boolean>(true);
const progressBarFields = ref();
const errorRef = ref<string>('');
const errorCode = ref<string>('');

const defaultState = {
  username: '',
  firstname: '',
  lastname: '',
  referralCode: '',
  idNumberType: 'idCard',
  idNumber: '',
  receivePromotionalInformation: false,
  terms: false,
  dateOfBirth: null,
};

const model = ref({ ...defaultState });

const progressBar = computed(() => {
  if (!progressBarFields.value) return 0;

  const excludeKeys = new Set([
    'idNumberType',
    'receivePromotionalInformation',
    'preferredLanguage',
    'referralCode',
    'welcomeOffer',
    'agreeToAll',
    'language',
  ]);

  const relevantFields = Object.entries(progressBarFields.value).filter(
    ([key]) => !excludeKeys.has(key),
  );

  if (relevantFields.length === 0) return 0;

  const validFields = relevantFields.filter(([name]) => {
    const el = model.value[name];
    return !!el && !form.value?.formErrors?.[name];
  });

  return validFields.length / relevantFields.length;
});

const handleError = (e: string) => {
  errorCode.value = e;
  errorRef.value = e;
};

const handleOnLoad = () => {
  loading.value = false;
  setTimeout(() => {
    handleProgressFields(progressBarFields, form);
  }, 500);
};

const register = async (forPartialReg = false) => {
  try {
    loading.value = true;
    errorCode.value = '';

    if (model.value.referralCode !== '')
      if (!/^[a-zA-Z0-9]{3,15}$/.test(model.value.referralCode)) {
        throw new Error('Invalid referralCode');
      }

    const deviceInfo = getUserDeviceInfo();
    const additionalProps = getAdditionalProps(
      deviceInfo,
      model,
      forPartialReg,
    );
    const request = {
      ...additionalProps,
      ...model.value,
      ...(model.value.referralCode === '' && { referralCode: null }),
      ...(!!model.value?.dateOfBirth && {
        dateOfBirth: auth.currentUser?.dateOfBirth,
      }),
    };

    for (const key in request) {
      if (request[key] === '') delete request[key];
    }

    await $trackEvent('RegistrationEvent', 'PARTIAL_REG_STEP_THREE', {
      msisdn: model?.value?.username,
    });
    const response = await $authService.completePartialReg(
      request as unknown as IPartialRegStepOneRequest,
    );

    if (response.isSuccessful) {
      handleSuccessfulReg(
        getActionId(),
        response as IRegisterResponse,
        model,
        errorCode,
      );
      await $authService.getUserExtended();
    } else {
      errorCode.value = $t(
        generateTranslationKey(`jpc-reg-${response?.error?.message}`) ||
          'general-error',
      );
      errorRef.value = $t(
        generateTranslationKey(response?.error?.code?.toString()) ||
          'general-error',
      );
      handleUnSuccessfulReg(
        response as IRegisterResponse,
        form,
        errorCode,
        errorRef,
      );
    }
  } catch (e) {
    if (e === 'deceased-or-minor-reg') errorCode.value = $t(e);
    else errorCode.value = $t('registration-failed');
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const lockFields = () => {
  model.value.username = auth.currentUser?.username.slice(
    site.getDialingCode.replace('+', '').length,
  );
  if (!!auth.currentUser?.dateOfBirth)
    if (typeof auth.currentUser?.dateOfBirth === 'string')
      model.value.dateOfBirth = new Date(auth.currentUser?.dateOfBirth);
    else model.value.dateOfBirth = auth.currentUser?.dateOfBirth;

  if (!!auth.currentUser?.firstname)
    model.value.firstname = auth.currentUser?.firstname.trim();

  if (!!auth.currentUser?.lastname)
    model.value.lastname = auth.currentUser?.lastname.trim() || '';

  nextTick(() => {
    if (
      !!model.value.firstname &&
      !!model.value.lastname &&
      !isNamesVerified.value
    ) {
      form.value?.schema?.sectionMapping.forEach((section) => {
        section?.elementMapping?.elements.forEach((element) => {
          if (element?.id === 'firstname' || element?.id === 'lastname') {
            isNamesVerified.value = true;
            element.disabled = true;
          }
        });
      });
    }
  });
};

watch(
  () => form.value?.schema,
  () => lockFields(),
);

onMounted(() => {
  lockFields();
});
</script>

<template>
  <div
    :class="['min-h-36 w-full bg-layer-secondary', { 'aspect-video': loading }]"
  >
    <UserInterfaceGenericLoader v-if="loading" container />
    <div class="bg-layer-secondary">
      <UserInterfaceRegProgressBar :progress="progressBar" />
      <UserInterfaceDynamicFormBuilder
        ref="form"
        v-model="model"
        template-name="complete registration"
        :container-classes="[{ 'px-3 py-2': true }]"
        :should-auto-focus="false"
        class="overflow-hidden"
        @loaded="handleOnLoad"
        @error="handleError"
        @edited="handleError('')"
      >
        <template #before>
          <UserInterfaceDynamicFormBuilderPseudoElements />
        </template>
        <template #form-actions="formActionProps">
          <GenericError
            v-if="errorCode"
            class="mb-3 max-w-72 mx-auto"
            state="danger"
            :reference-code="errorRef"
          >
            {{ errorCode }}
          </GenericError>
          <div class="w-full p-4 bg-base">
            <div
              v-if="!formActionProps?.form?.schema?.hasSteps"
              class="flex flex-col gap-1"
            >
              <Button
                element-name="jpc-register"
                group="primary"
                class="w-full justify-center"
                :disabled="
                  !(
                    formActionProps?.form?.validated &&
                    !formActionProps?.form?.invalid
                  )
                "
                @click="register"
              >
                {{ $t('jpc-register') }}
              </Button>
            </div>
          </div>
        </template>
      </UserInterfaceDynamicFormBuilder>
    </div>
  </div>
</template>
